import { FormInstance, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Divider,
  PhoneInput,
  Gutter,
  Form,
  Col,
  Row,
  Input,
} from '@common/components';
import { WorkforceGroupSelect } from '@scheduling/components';
import { MembershipByEmail } from 'works/legacy/graphql/works-membership-by-email';
import { RolesQuery } from '@clinician-profile/graphql';
import { useQuery } from '@common/hooks';
import { AllowListSelect } from './_allow-list-select';

export function WorkerCreateForm({
  form,
  membershipFound,
}: {
  form: FormInstance;
  membershipFound?: MembershipByEmail;
}) {
  const [selectedRoleId, setSelectedRoleId] = useState();

  const { data: rolesData, loading: rolesLoading } = useQuery(RolesQuery);

  const roleOptions = rolesData?.roles
    .map((role) => ({
      label: role.abbreviation,
      value: role.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // NOTE: CreateWorker only fills a blank employee_id on an existing worker
  // membership, so a value edited here would be silently discarded.
  const loadBalancingOnlyWorkerMembership =
    !!membershipFound && membershipFound.role === 'worker';

  useEffect(() => {
    if (membershipFound) {
      form.setFieldsValue({
        firstName: membershipFound.firstName,
        lastName: membershipFound.lastName,
        phone: membershipFound.phone,
        ...(loadBalancingOnlyWorkerMembership
          ? { employeeId: membershipFound.employeeId }
          : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipFound]);

  return (
    <>
      <Form.Item name="sendAppInvite" valuePropName="checked">
        <Checkbox>Send invite upon creation</Checkbox>
      </Form.Item>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter a first name' }]}
          >
            <Input
              size="large"
              placeholder="Enter First Name"
              disabled={!!membershipFound}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter a last name' }]}
          >
            <Input
              size="large"
              placeholder="Enter Last Name"
              disabled={!!membershipFound}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="Employee ID"
            name="employeeId"
            rules={[{ required: true, message: 'Please enter an Employee ID' }]}
          >
            <Input
              size="large"
              placeholder="Enter Employee ID"
              disabled={loadBalancingOnlyWorkerMembership}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Mobile Number"
            name="phone"
            rules={
              membershipFound
                ? []
                : [
                    {
                      pattern: /^(\d{3}-){2}\d{4}$/,
                      message: 'Must be in xxx-xxx-xxxx format',
                    },
                  ]
            }
          >
            <PhoneInput
              type="text"
              size="large"
              placeholder="Enter Mobile Number"
              disabled={!!membershipFound}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="Role"
            name="roleId"
            rules={[
              { required: true, message: "Please enter the worker's role" },
            ]}
          >
            <Select
              placeholder="Select Role"
              size="large"
              showSearch
              options={roleOptions}
              loading={rolesLoading}
              optionFilterProp="label"
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value) => setSelectedRoleId(value)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <WorkforceGroupSelect />
        </Col>
      </Row>

      <Divider />

      <Gutter size="lg" direction="top">
        <AllowListSelect
          form={form}
          disabled={!selectedRoleId}
          selectedRoleId={selectedRoleId}
          roleOptions={roleOptions}
        />
      </Gutter>
    </>
  );
}
